import { Component, Input, Output, EventEmitter, HostListener, ElementRef } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, Params } from '@angular/router';
import { Course, SectionContentSession, CourseSubscriptionSession, CourseSubscriptionSessionProvider, StatisticsProvider } from '@stuplay';
import { NavigationProvider } from '../../services/navigation.provider';
import { OrderedService } from '../../services/ordered.service';
import { StatsService } from '../../services/stats.service';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { StorageService } from '../../services/storage.service';
import { EmitterService } from '../../services/emitter.service';
import { BackUrlService } from '../../services/back-url.service';

@Component({
    selector: 'msc-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.less'],
})
export class SidebarComponent {
    @Input() course: Course;
    @Input() showSidebar: any;
    @Input() external: boolean = false;
    @Output() closeSidebar: EventEmitter<any> = new EventEmitter;
    courseSubscriptionSession: CourseSubscriptionSession;
    sectionOpen: Array<boolean> = [];
    exerciseOpen: { [id: number]: boolean } = {};
    questionsSessions: any[] = [];
    sectionNumber: any;
    loading: boolean = false;
    preview: boolean;
    currentSectionContentId: number;
    displayNav;
    public logo: string = '';
    // Progreso GENERAL desde reporting (autoritativo, 0-100). null = aún no cargado.
    public reportProgress: number = null;

    constructor(
        private navProvider: NavigationProvider,
        private eRef: ElementRef,
        private route: ActivatedRoute,
        private router: Router,
        private orderedService: OrderedService,
        private statsService: StatsService,
        private courseSubscriptionSessionProvider: CourseSubscriptionSessionProvider,
        private modalService: NgbModal,
        private storageService: StorageService,
        private emitterService: EmitterService,
        private fromService: BackUrlService,
        private statisticsProvider: StatisticsProvider,
    ) {
        this.courseSubscriptionSession = this.navProvider?.currentCourseSubscriptionSession;
        this.orderedService.setSession(this.courseSubscriptionSession);
        this.orderedService.setSection(this.navProvider?.currentCourse?.sections);
        this.setQuestionsSessions();
        this.route.queryParams.subscribe((params: Params) => {
            this.preview = params['preview'];
        });
    }

    ngOnInit() {
        this.route.children[0].params.subscribe((data) => {
            this.sectionNumber = data?.sectionId;
            this.course.sections.forEach((section) => {
                this.openSection(section);
            });
        });

        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.displayNav = false;
                if (event.url.match('/section/')) {
                    this.displayNav = !this.course.isTrajectContent || (this.course.isTrajectContent && this.course.templateId);
                    this.sectionNumber = event.url.replace('?preview=true', '').split('/section/')[1].split('/')[0];
                    this.course.sections.forEach((section) => {
                        this.openSection(section);
                    });
                }
            }
        });

        this.statsService.listenRefresh().subscribe((refreshed) => {
            // Quien ya recargó la sesión (ActivityExitCoordinator) la envía para no pedirla dos veces.
            if (refreshed instanceof CourseSubscriptionSession) {
                this.courseSubscriptionSession = this.navProvider.currentCourseSubscriptionSession = refreshed;
                this.orderedService.setSession(this.courseSubscriptionSession);
                return;
            }

            this.courseSubscriptionSessionProvider.getOrCreateCourseSubscriptionSession(this.course?.id, this.preview).subscribe((data) => {
                this.courseSubscriptionSession = this.navProvider.currentCourseSubscriptionSession = data;
                this.orderedService.setSession(this.courseSubscriptionSession);
            });
        });

        this.logo = `${this.storageService.get('company').experience.displayPicture}?size=512`;
        this.loadReportProgress();
    }

    /**
     * Carga el progreso GENERAL desde el servicio de reporting (fuente autoritativa).
     */
    private loadReportProgress(): void {
        const companyId = this.storageService.get('company')?.id;
        const accountId = this.storageService.get('me')?.id;
        const courseId = this.course?.id;
        if (!companyId || !accountId || !courseId || this.preview) { return; }

        this.statisticsProvider.getContentSubscriptionReport(companyId, courseId, accountId).subscribe((report) => {
            if (report) { this.reportProgress = report.progress; }
        });
    }

    setQuestionsSessions(): void {
        this.questionsSessions = [];
        this.courseSubscriptionSession.sectionContentSessions.forEach((sectionContentSession: SectionContentSession) => {
            sectionContentSession.questionSessions.forEach((questionSession) => {
                this.questionsSessions.push(questionSession);
            });
        });
    }

    back(): void {
        const me = this.storageService.get('me');
        const company = this.storageService.get('company');
        parent.postMessage('closeIframePlayer', '*');
//        document.location.replace(this.fromService.getBackUrl());
    }

    navigate(sectionContent: any, section: any): any {
        switch (sectionContent?.context) {
            case 'media':
                return ['section', section?.id, sectionContent?.context, sectionContent?.media?.id, 'content', sectionContent?.id];
            case 'external_link':
                return ['section', section?.id, 'external-link', sectionContent?.externalLink?.id];
            default:
                return ['section', section?.id, sectionContent?.context, sectionContent[sectionContent?.context]?.id];
        }
    }

    openSection(section: any, origin?: string): void {
        this.scroller();
        const sectionIndex = this.course.sections.findIndex((elementSection) => elementSection?.id === section?.id);
        if (origin === 'template') {
            // Acordeón de uno solo: guardamos el estado previo, cerramos todos y
            // dejamos abierto únicamente el último seleccionado (toggle sobre sí mismo).
            const wasOpen = this.sectionOpen[sectionIndex];
            this.sectionOpen = [];
            this.sectionOpen[sectionIndex] = !wasOpen;
        } else {
            if (this.sectionNumber === (section?.id).toString()) {
                // Al navegar a una sección, dejar abierta solo esa.
                this.sectionOpen = [];
                this.sectionOpen[sectionIndex] = true;
            }
        }

        this.emitterService.set('sectionOpen', true);
    }

    /**
     * Indica si el menú de un ejercicio está expandido. Por defecto se muestra
     * colapsado, salvo que el ejercicio sea el que está activo en la ruta actual
     * (para que sus preguntas queden visibles al navegar a él).
     */
    isExerciseOpen(exerciseId: number): boolean {
        if (this.exerciseOpen.hasOwnProperty(exerciseId)) {
            return this.exerciseOpen[exerciseId];
        }
        return this.router.url.includes('/exercise/' + exerciseId + '/');
    }

    toggleExercise(exerciseId: number): void {
        this.exerciseOpen[exerciseId] = !this.isExerciseOpen(exerciseId);
    }

    getClasses(questionId: number): string {
        this.setQuestionsSessions();
        const questionSession = this.questionsSessions.find((questionSession) => questionSession?.questionId === questionId);
        if (questionSession) {
            const done: string = `${questionSession.doneAt && 'is-done'}`;
            const correction: string = `${(questionSession.doneAt && questionSession.isCorrect) ? 'success' : 'fail'}`;
            if (questionSession.type !== 'open-write' && done && correction) {
                return done + ' ' + correction
            } else if (questionSession.type === 'open-write' && questionSession.doneAt && !questionSession.correctedAt) {
                return 'pending';
            } else if (questionSession.type === 'open-write' && questionSession.doneAt && questionSession.correctedAt) {
                return 'is-done ' + (questionSession.isCorrect ? 'success' : 'fail');
            } else {
                return '';
            }
        }
    }

    getQuestionState(questionId: number): 'success' | 'fail' | 'pending' | null {
        this.setQuestionsSessions();
        const qs = this.questionsSessions.find((s) => s?.questionId === questionId);
        if (!qs || !qs.doneAt) { return null; }
        if (qs.type === 'open-write' && !qs.correctedAt) { return 'pending'; }
        return qs.isCorrect ? 'success' : 'fail';
    }

     // li
    getSectionContentClasses(sectionContentId: number): string {
        const scs = this.courseSubscriptionSession.sectionContentSessions
            .find((s) => s?.sectionContentId === sectionContentId);
        return scs?.doneAt ? 'is-done success' : '';
    }

    isContentDone(sectionContentId: number): boolean {
        const scs = this.courseSubscriptionSession?.sectionContentSessions
            ?.find((s) => s?.sectionContentId === sectionContentId);
        return !!scs?.doneAt;
    }

    getSectionState(section: any, index: number): 'completed' | 'active' | 'disabled' | 'pending' {
        if (this.isDisabled(section)) {
            return 'disabled';
        }
        const progress = this.courseSubscriptionSession?.getSectionProgress(section) || 0;
        if (progress >= 1) {
            return 'completed';
        }
        if (this.sectionOpen[index]) {
            return 'active';
        }
        return 'pending';
    }

    getOverallProgress(): number {
        // Si todas las secciones están completadas localmente, forzar 100%
        if (this.course?.sections?.length > 0) {
            const allDone = this.course.sections.every((s: any) =>
                this.courseSubscriptionSession?.getSectionProgress(s) >= 1
            );
            if (allDone) { return 1; }
        }
        // Progreso GENERAL autoritativo desde reporting (si ya cargó). Normalizado a 0-1.
        if (this.reportProgress != null) {
            return this.reportProgress > 1 ? this.reportProgress / 100 : this.reportProgress;
        }
        const raw = this.courseSubscriptionSession?.progress;
        if (raw == null) { return 0; }
        return raw > 1 ? raw / 100 : raw;
    }

    getTotalLessons(): number {
        if (!this.course?.sections) { return 0; }
        return this.course.sections.reduce((acc: number, s: any) => acc + (s?.sectionContents?.length || 0), 0);
    }

    getCompletedLessons(): number {
        if (!this.courseSubscriptionSession?.sectionContentSessions) { return 0; }
        return this.courseSubscriptionSession.sectionContentSessions.filter((s: any) => !!s?.doneAt).length;
    }

    getProgressColor(progress: number): string {
        // Definimos el color inicial y final utilizando variables CSS
        const startColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim();
        const middleColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-600').trim();
        const endColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-700').trim();
    
        // Calculamos el color intermedio utilizando una interpolación lineal entre los colores
        const interpolatedColor = this.interpolateColor(startColor, endColor, progress);
        return interpolatedColor;
    }
    getVerticalProgressGradient(progress: number): string {
        const startColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim();
        const endColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-700').trim();
    
        // Calculamos el porcentaje de progreso en términos de degradado vertical
        const progressPercentage = progress * 100;
    
        // Creamos un gradiente vertical basado en el progreso
        return `linear-gradient(to bottom, ${startColor} ${progressPercentage}%, #e3e3e3 ${progressPercentage}%)`;
    }
    
    //getClass(progress: number): string { return (progress == 1.0-0) }
    
    
    interpolateColor(color1: string, color2: string, weight: number): string {
        // Convertimos los colores de formato hexadecimal a sus componentes RGB
        const r1 = parseInt(color1.substring(1, 3), 16);
        const g1 = parseInt(color1.substring(3, 5), 16);
        const b1 = parseInt(color1.substring(5, 7), 16);
    
        const r2 = parseInt(color2.substring(1, 3), 16);
        const g2 = parseInt(color2.substring(3, 5), 16);
        const b2 = parseInt(color2.substring(5, 7), 16);
    
        // Calculamos los componentes RGB del color intermedio
        const r = Math.round(r1 * (1 - weight) + r2 * weight);
        const g = Math.round(g1 * (1 - weight) + g2 * weight);
        const b = Math.round(b1 * (1 - weight) + b2 * weight);
    
        // Convertimos los componentes RGB nuevamente a formato hexadecimal
        const interpolatedColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    
        return interpolatedColor;
    }
    
    getIconesClasses(sectionContentContext: string): string {
        switch (sectionContentContext) {
            case 'cheatsheet':
                return 'icon icon-link-outline';
            case 'discussion':
                return 'icon icon-message-square-outline';
            case 'event':
                return 'icon icon-icon_calendar';
            case 'media':
                return 'icon icon-media';
            case 'project':
            case 'selfassessment':
                return 'icon icon-courses';
            case 'scorm':
            case 'scormcloud':
            case 'external_link':
                return 'icon icon-link-outline';
            case 'exchange':
                return 'icon icon-exchange';
            case 'certificate':
                return 'icon icon-certificate';
        }
    }

    /**
     * Maps a sectionContent to a logical icon key used by the sidebar template.
     * Differentiates media by mime/type so videos get a play icon, audios get headphones, etc.
     */
    getContentIconKey(sectionContent: any): string {
        const context = sectionContent?.context;
        if (context === 'media') {
            const m = sectionContent?.media;
            const type = (m?.type || m?.kind || '').toString().toLowerCase();
            const mime = (m?.mimeType || m?.mime || '').toString().toLowerCase();
            if (type.includes('video') || mime.includes('video')) { return 'video'; }
            if (type.includes('audio') || mime.includes('audio')) { return 'audio'; }
            if (type.includes('pdf') || mime.includes('pdf')) { return 'pdf'; }
            if (type.includes('image') || mime.includes('image')) { return 'image'; }
            return 'media';
        }
        switch (context) {
            case 'cheatsheet': return 'document';
            case 'discussion': return 'discussion';
            case 'exchange': return 'exchange';
            case 'event': return 'event';
            case 'project':
            case 'selfassessment': return 'project';
            case 'scorm':
            case 'scormcloud': return 'scorm';
            case 'external_link': return 'link';
            case 'certificate': return 'certificate';
            case 'exercise': return 'exercise';
            default: return 'default';
        }
    }

    /**
     * Indica si algún módulo desplegado muestra más de 5 submenús (contando las
     * preguntas de un ejercicio abierto). En ese caso el sidebar activa el alto
     * fijo (100vh) y el scroll interno; con 5 o menos crece de forma natural.
     */
    isSubmenuOverflowing(): boolean {
        if (!this.course?.sections) { return false; }
        return this.course.sections.some((section: any, index: number) => {
            if (!this.sectionOpen[index] || this.isDisabled(section)) { return false; }
            const contents = section?.sectionContents || [];
            let count = contents.length;
            contents.forEach((sc: any) => {
                if (sc?.context === 'exercise' && this.isExerciseOpen(sc?.exercise?.id)) {
                    count += (sc?.exercise?.questions?.length || 0);
                }
            });
            return count > 5;
        });
    }

    isDisabled(section: any): boolean {
        if (!this.course.isOrdered) {
            return false;
        }

        this.orderedService.setSection(this.navProvider.currentCourse.sections);

        return !this.orderedService.isSectionExist(section?.id);
    }

    toggleSidebar() {
        this.closeSidebar.emit();
    }

    /**
     * Cierra el drawer en mobile quitando la misma clase DOM con la que lo abre
     * el botón de heading-actions (.course-layout.sidebar-open). Se evita pasar
     * por el booleano showSidebar para que la X cierre al primer clic (antes el
     * binding [class.sidebar-open] volvía a agregar la clase en el primer toque).
     */
    closeMobileDrawer() {
        const layout = document.querySelector('.course-layout');
        if (layout) { layout.classList.remove('sidebar-open'); }
    }

    collapseDesktop(event?: Event) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        document.body.classList.toggle('sidebar-collapsed');
    }

    @HostListener('document:click', ['$event'])
    clickout(event: any) {
        // En mobile, al elegir un contenido dentro del drawer, ciérralo quitando
        // la clase DOM .sidebar-open (misma fuente de verdad que usan la flecha de
        // heading-actions y el backdrop). Antes emitía closeSidebar y volteaba el
        // booleano showSidebar, lo que dejaba la capa oscura pegada y bloqueando.
        if (this.eRef.nativeElement.contains(event.target)) {
            this.closeMobileDrawer();
        }
    }

    scroller(): void {
        const timeOut = setTimeout(() => {
            if (document?.getElementById('view')?.scroll) {
                document.getElementById('view').scroll(0, 0);
            }
            clearTimeout(timeOut);
        }, 50);
    }

    openModalReset(id: number, content: any): void {
        this.currentSectionContentId = id;
        const options: NgbModalOptions = { size: 'lg', backdropClass: 'backdrop-custom' };
        this.modalService.open(content, options);
    }

    resetQuiz(): void {
        this.loading = true;
        const sectionContentSession = this.courseSubscriptionSession.sectionContentSessions.find((sectionContentSession) => {
            return sectionContentSession.sectionContentId === this.currentSectionContentId;
        });

        this.courseSubscriptionSessionProvider.resetContentSectionSession(sectionContentSession?.id).subscribe(() => {
            window.location.reload();
        });
    }
}
